/**
 * TerminalOutput - xterm.js terminal display component
 *
 * Renders a read-only terminal for displaying code execution output.
 * Uses xterm.js for proper ANSI escape code handling (colors, cursor, etc.).
 *
 * Features:
 * - Read-only mode (no stdin)
 * - Catppuccin-inspired dark theme
 * - Auto-resize to container via ResizeObserver
 * - Imperative API for parent control (write/writeLine/clear)
 *
 * Usage:
 * ```tsx
 * const terminalRef = useRef<TerminalOutputHandle>(null);
 *
 * // Write to terminal
 * terminalRef.current?.write('Hello\n');
 * terminalRef.current?.writeLine('World');
 *
 * // Clear terminal
 * terminalRef.current?.clear();
 *
 * return <TerminalOutput ref={terminalRef} className="h-32" />;
 * ```
 */

import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Imperative handle for controlling the terminal from parent components
 */
export interface TerminalOutputHandle {
  /** Write text to terminal (raw, no newline added) */
  write: (data: string) => void;

  /** Write a line to terminal (adds newline at end) */
  writeLine: (data: string) => void;

  /** Clear all terminal content */
  clear: () => void;
}

/**
 * Props for the TerminalOutput component
 */
interface TerminalOutputProps {
  /** Additional CSS classes for the container */
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * TerminalOutput Component
 *
 * A read-only terminal display for streaming code execution output.
 * Uses xterm.js for proper terminal emulation with ANSI support.
 *
 * The component exposes write/writeLine/clear methods via ref for parent control.
 */
export const TerminalOutput = forwardRef<TerminalOutputHandle, TerminalOutputProps>(
  ({ className = '' }, ref) => {
    // ==================== REFS ====================

    // DOM element for terminal mounting
    const terminalRef = useRef<HTMLDivElement>(null);

    // xterm.js Terminal instance
    const xtermRef = useRef<Terminal | null>(null);

    // FitAddon for auto-resizing
    const fitAddonRef = useRef<FitAddon | null>(null);

    // ==================== TERMINAL SETUP ====================

    useEffect(() => {
      // Guard: ensure DOM element exists
      if (!terminalRef.current) return;

      // Create terminal with Catppuccin-inspired dark theme
      // This matches the dark code editor aesthetic
      const terminal = new Terminal({
        cursorBlink: false,      // No blinking cursor (read-only)
        disableStdin: true,       // Disable keyboard input (read-only output)
        fontSize: 13,
        fontFamily: 'JetBrains Mono, Menlo, Monaco, Consolas, monospace',
        lineHeight: 1.2,
        theme: {
          // Catppuccin Mocha-inspired colors
          background: '#1e1e2e',   // Dark background
          foreground: '#cdd6f4',   // Light text
          cursor: '#f5e0dc',       // Light cursor
          // Standard terminal colors
          black: '#45475a',
          red: '#f38ba8',          // Errors
          green: '#a6e3a1',        // Success
          yellow: '#f9e2af',       // Warnings
          blue: '#89b4fa',
          magenta: '#cba6f7',
          cyan: '#94e2d5',
          white: '#bac2de',
          // Bright variants
          brightBlack: '#585b70',
          brightRed: '#f38ba8',
          brightGreen: '#a6e3a1',
          brightYellow: '#f9e2af',
          brightBlue: '#89b4fa',
          brightMagenta: '#cba6f7',
          brightCyan: '#94e2d5',
          brightWhite: '#a6adc8',
        },
      });

      // Create and load fit addon for auto-resize
      const fitAddon = new FitAddon();
      terminal.loadAddon(fitAddon);

      // Mount terminal to DOM
      terminal.open(terminalRef.current);

      // Initial fit to container size
      fitAddon.fit();

      // Store refs for cleanup and imperative handle
      xtermRef.current = terminal;
      fitAddonRef.current = fitAddon;

      // ==================== RESIZE HANDLING ====================

      // Use ResizeObserver to auto-fit terminal when container resizes
      // This handles node resizing, window resizing, etc.
      const resizeObserver = new ResizeObserver(() => {
        // Use requestAnimationFrame to debounce resize operations
        requestAnimationFrame(() => {
          fitAddon.fit();
        });
      });

      resizeObserver.observe(terminalRef.current);

      // ==================== CLEANUP ====================

      return () => {
        resizeObserver.disconnect();
        terminal.dispose();
      };
    }, []);

    // ==================== IMPERATIVE HANDLE ====================

    // Expose write/writeLine/clear methods to parent via ref
    // This allows parent component (CodeNode) to control the terminal
    useImperativeHandle(ref, () => ({
      /**
       * Write raw text to terminal (no automatic newline)
       */
      write: (data: string) => {
        xtermRef.current?.write(data);
      },

      /**
       * Write a line to terminal (adds newline at end)
       */
      writeLine: (data: string) => {
        xtermRef.current?.writeln(data);
      },

      /**
       * Clear all terminal content
       */
      clear: () => {
        xtermRef.current?.clear();
      },
    }), []);

    // ==================== RENDER ====================

    return (
      <div
        ref={terminalRef}
        className={`rounded-lg overflow-hidden ${className}`}
        style={{
          minHeight: 120,
          // Ensure terminal background shows through any transparent areas
          backgroundColor: '#1e1e2e',
        }}
      />
    );
  }
);

// Display name for React DevTools debugging
TerminalOutput.displayName = 'TerminalOutput';
